import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { LakeUser, UserControllerService } from '@api/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { toOptionItemModel } from '@shared/components/multi-select-with-exclude/multi-select-with-exclude.utils';
import { UserRollEnum } from '@shared/enums/user-roll';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { keyBy } from 'lodash';
import { pipe, switchMap } from 'rxjs';

export const useUsers = () =>
  signalStoreFeature(
    {
      props: {
        _notificationService: type<NotificationService>(),
      },
    },
    withState({
      users: new Array<LakeUser>(),
      usersLoaded: false,
    }),
    withProps(() => ({
      _usersService: inject(UserControllerService),
    })),
    withComputed(store => {
      const usersMap = computed(() => keyBy(store.users(), 'id'));

      const usersOptions = computed(() => store.users().map(lp => toOptionItemModel(lp, 'id', 'login')));

      const admins = computed(() =>
        store.users().filter(user => user.role && UserRollEnum.ADMIN_ROLES.includes(user.role)),
      );

      const adminOptions = computed(() => admins().map(admin => toOptionItemModel(admin, 'id', 'login')));

      return {
        admins,
        adminOptions,
        usersMap,
        usersOptions,
      };
    }),
    withMethods(store => {
      const loadUsers = rxMethod<void>(
        pipe(
          switchMap(() =>
            store._usersService.getAllUsers().pipe(
              tapResponse({
                next: (users: LakeUser[]) => patchState(store, { users, usersLoaded: true }),
                error: (error: HttpErrorResponse) => {
                  store._notificationService.showErrorNotification('Failed to load users', error);
                  patchState(store, { usersLoaded: true });
                },
              }),
            ),
          ),
        ),
      );
      return {
        loadUsers,
      };
    }),
  );
